import React, { createContext, useContext, useState, useEffect } from 'react'
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { auth, db } from '../firebase'
import { roleService } from '../services/roleService'
import toast from 'react-hot-toast'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [role, setRole] = useState(null)
  const [branch, setBranch] = useState(null)
  const [profileError, setProfileError] = useState(null)

  function normalizeRole(value) {
    const r = String(value || '').trim()
    if (!r) return null
    const lower = r.toLowerCase()
    return lower === 'admin' ? 'owner' : lower
  }

  async function hydrateProfile(profile) {
    if (!profile) return null
    try {
      const roleKey = normalizeRole(profile.role) || profile.role
      // Customers have no console permissions — skip Firestore roles read.
      if (roleKey === 'customer') {
        return {
          ...profile,
          role: roleKey,
          rolePermissions: [],
          permissions: Array.isArray(profile.permissions) ? profile.permissions : [],
        }
      }
      const rolePermissions = await roleService.resolvePermissionsForRole(roleKey, [])
      const mergedPermissions = [
        ...new Set([...(profile.permissions || []), ...rolePermissions]),
      ]
      return {
        ...profile,
        role: roleKey,
        rolePermissions,
        permissions: mergedPermissions,
      }
    } catch (e) {
      console.warn('Could not hydrate role permissions:', e)
      return profile
    }
  }

  // Real Firebase authentication listener
  useEffect(() => {
    console.log('🚀 Production mode - Setting up Firebase authentication')

    // Never leave the app on the initial spinner forever if auth hangs
    const bootTimeout = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn('[AuthContext] auth boot timeout — clearing loading state')
          setProfileError('Sign-in is taking too long. Please refresh or try again.')
        }
        return false
      })
    }, 15000)

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user)
      setProfileError(null)

      if (user) {
        // Load user profile from Firestore
        try {
          // Ensure the auth token is available before Firestore reads (avoids transient permission-denied).
          try {
            await user.getIdToken()
          } catch (e) {
            console.warn('Could not prefetch ID token:', e)
          }

          const userDoc = await getDoc(doc(db, 'users', user.uid))
          if (userDoc.exists()) {
            const profile = await hydrateProfile(userDoc.data())
            setUserProfile(profile)
            const nextRole = normalizeRole(profile.role)
            setRole(nextRole)
            setBranch(profile.branchId)
            if (!nextRole) {
              setProfileError('Your account has no role assigned. Please contact an administrator.')
            }
            console.info('[AuthContext] profile loaded', {
              uid: user.uid,
              email: user.email,
              role: profile.role,
              normalizedRole: nextRole,
              branchId: profile.branchId ?? null,
              permissions: profile.permissions,
            })
          } else {
            // Create initial profile for new users
            const newProfile = {
              email: user.email,
              displayName: user.displayName || user.email.split('@')[0],
              role: 'owner', // First user becomes owner
              isActive: true,
              createdAt: new Date(),
              phone: '' // Never insert dummy phone in production
            }
            await setDoc(doc(db, 'users', user.uid), newProfile)
            const hydrated = await hydrateProfile(newProfile)
            setUserProfile(hydrated)
            setRole('owner')
            setBranch(null)
            console.info('[AuthContext] profile created', { uid: user.uid, email: user.email, role: 'owner' })
          }
        } catch (error) {
          // If Firestore fires before auth is fully wired, retry once after a token refresh.
          const code = error?.code || ''
          if (String(code).includes('permission-denied')) {
            try {
              await user.getIdToken(true)
              const userDoc = await getDoc(doc(db, 'users', user.uid))
              if (userDoc.exists()) {
                const profile = await hydrateProfile(userDoc.data())
                setUserProfile(profile)
                const nextRole = normalizeRole(profile.role)
                setRole(nextRole)
                setBranch(profile.branchId)
                if (!nextRole) {
                  setProfileError('Your account has no role assigned. Please contact an administrator.')
                }
              } else {
                setProfileError('Account profile not found. Please contact support.')
              }
            } catch (retryError) {
              console.error('Error loading user profile (retry):', retryError)
              setProfileError('Failed to load user profile. Please sign out and try again.')
              toast.error('Failed to load user profile')
            }
          } else {
            console.error('Error loading user profile:', error)
            setProfileError('Failed to load user profile. Please refresh or sign out.')
            toast.error('Failed to load user profile')
          }
        }
      } else {
        setUserProfile(null)
        setRole(null)
        setBranch(null)
        setProfileError(null)
      }

      clearTimeout(bootTimeout)
      setLoading(false)
    })

    return () => {
      clearTimeout(bootTimeout)
      unsubscribe()
    }
  }, [])

  // Sign in user
  async function signIn(email, password) {
    try {
      // Real Firebase authentication
      const result = await signInWithEmailAndPassword(auth, email, password)
      toast.success('Signed in successfully!')
      return result
    } catch (error) {
      console.error('Sign in error:', error)
      let message = 'Failed to sign in'
      
      switch (error.code) {
        case 'auth/user-not-found':
          message = 'No account found with this email'
          break
        case 'auth/wrong-password':
          message = 'Invalid password'
          break
        case 'auth/invalid-email':
          message = 'Invalid email address'
          break
        case 'auth/too-many-requests':
          message = 'Too many failed attempts. Try again later'
          break
        default:
          message = error.message
      }
      
      toast.error(message)
      throw error
    }
  }

  // Sign up new user
  async function signUp(email, password, userData = {}) {
    try {
      // Create Firebase user
      const result = await createUserWithEmailAndPassword(auth, email, password)
      
      // Create user profile in Firestore
      const userProfile = {
        email: email,
        displayName: userData.name || email.split('@')[0],
        role: userData.role || 'owner',
        phone: userData.phone || '',
        isActive: true,
        createdAt: new Date(),
        ...userData
      }
      
      await setDoc(doc(db, 'users', result.user.uid), userProfile)
      toast.success('Account created successfully!')
      return result
    } catch (error) {
      console.error('Sign up error:', error)
      let message = 'Failed to create account'
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          message = 'An account with this email already exists'
          break
        case 'auth/weak-password':
          message = 'Password should be at least 6 characters'
          break
        case 'auth/invalid-email':
          message = 'Invalid email address'
          break
        default:
          message = error.message
      }
      
      toast.error(message)
      throw error
    }
  }

  // Sign out user
  async function signOut() {
    try {
      await firebaseSignOut(auth)
      toast.success('Signed out successfully')
    } catch (error) {
      console.error('Sign out error:', error)
      toast.error('Failed to sign out')
      throw error
    }
  }

  // Reset password
  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email)
      toast.success('Password reset email sent')
    } catch (error) {
      console.error('Reset password error:', error)
      toast.error('Failed to send reset email')
      throw error
    }
  }

  // Update user profile
  async function updateUserProfile(updates) {
    try {
      if (!currentUser) throw new Error('No authenticated user')
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        ...updates,
        updatedAt: new Date()
      })
      
      setUserProfile(prev => ({ ...prev, ...updates }))
      if (Object.prototype.hasOwnProperty.call(updates, 'branchId')) {
        setBranch(updates.branchId || null)
      }
      if (Object.prototype.hasOwnProperty.call(updates, 'role')) {
        setRole(normalizeRole(updates.role))
      }
      toast.success('Profile updated successfully')
    } catch (error) {
      console.error('Update profile error:', error)
      toast.error('Failed to update profile')
      throw error
    }
  }

  // Change password
  async function changePassword(currentPassword, newPassword) {
    try {
      if (!currentUser) throw new Error('No authenticated user')
      
      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword)
      await reauthenticateWithCredential(currentUser, credential)
      
      // Update password
      await updatePassword(currentUser, newPassword)
      toast.success('Password updated successfully')
    } catch (error) {
      console.error('Change password error:', error)
      let message = 'Failed to update password'
      
      if (error.code === 'auth/wrong-password') {
        message = 'Current password is incorrect'
      }
      
      toast.error(message)
      throw error
    }
  }

  // Check if user has required role
  function hasRole(requiredRoles) {
    if (!role) return false
    return Array.isArray(requiredRoles) ? requiredRoles.includes(role) : role === requiredRoles
  }

  // Check if user has access to branch
  function hasBranchAccess(branchId) {
    if (role === 'owner') return true
    return branch === branchId
  }

  // Create user profile (for registration)
  async function createUserProfile(uid, userData) {
    try {
      await setDoc(doc(db, 'users', uid), {
        ...userData,
        createdAt: new Date()
      })
      
      toast.success('User profile created successfully')
    } catch (error) {
      console.error('Create profile error:', error)
      toast.error('Failed to create user profile')
      throw error
    }
  }

  const value = {
    currentUser,
    userProfile,
    role,
    branch,
    loading,
    profileError,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateUserProfile,
    changePassword,
    hasRole,
    hasBranchAccess,
    createUserProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export { AuthProvider } 